import { css, styled } from 'next-yak';

import { IconArrowDown, IconTick, IconWarning } from '@xing-com/icons';
import {
  cornerRadiusM,
  scale005,
  scale010,
  scale050,
  scale060,
  scale070,
  scale080,
  scale150,
  scale160,
  scale170,
  scale100,
  scale110,
  spaceL,
  spaceM,
  spaceXS,
  spaceXXL,
  spaceXXS,
  xdlColorA11yBorder,
  xdlColorBackground,
  xdlColorControlBorder,
  xdlColorControlBorderSecondary,
  xdlColorControlFill,
  xdlColorControlFillSecondary,
  xdlColorDisabledSoft,
  xdlColorDisabledStrong,
  xdlColorError,
  xdlColorFocusRing,
  xdlColorSuccess,
  xdlColorText,
  xdlColorTextSecondary,
} from '@xing-com/tokens';
import { Meta } from '@xing-com/typography';

import type { DropdownSize, DropdownVariant } from './dropdown.types';
import { Option as OptionXdl } from './option';

type DropdownTProps = {
  $error?: boolean;
  $isDisabled?: boolean;
  $isFilled?: boolean;
  $size: DropdownSize;
  $success?: boolean;
  $variant?: DropdownVariant;
};

type LabelTProps = {
  $isDisabled?: boolean;
  $isFilled: boolean;
  $size: DropdownSize;
};

type HelperTextTProps = {
  $error?: boolean;
  $isDisabled?: boolean;
};

type IconTProps = {
  $isDisabled?: boolean;
  $isFilled?: boolean;
  $size: DropdownSize;
};

export const Wrapper = styled.div`
  width: 100%;
`;

export const DropdownWrapper = styled.div`
  position: relative;
`;

const styles = css`
  background-color: ${xdlColorControlFillSecondary};
  border-block-end: ${scale010} solid ${xdlColorControlBorder};
  border-block-start: ${scale005} solid ${xdlColorA11yBorder};
  border-inline-end: ${scale005} solid ${xdlColorA11yBorder};
  border-inline-start: ${scale005} solid ${xdlColorA11yBorder};
  border-radius: ${cornerRadiusM} ${cornerRadiusM} 0 0;
  color: ${xdlColorTextSecondary};
  display: inline-block;
  outline: none;
  padding-inline-end: ${spaceM};
  padding-inline-start: ${spaceL};
  width: 100%;

  @media (prefers-contrast: more) {
    & {
      border-block-end-width: 3px !important;
      border-block-start-width: ${scale010} !important;
      border-inline-end-width: ${scale010} !important;
      border-inline-start-width: ${scale010} !important;
    }
  }

  & option {
    color: ${xdlColorTextSecondary} !important;
  }

  &:focus {
    && {
      background-color: ${xdlColorControlFill};
      border-block-end: ${scale010} solid ${xdlColorControlBorderSecondary};
      border-block-start: ${scale005} solid ${xdlColorControlBorder};
      border-inline-end: ${scale005} solid ${xdlColorControlBorder};
      border-inline-start: ${scale005} solid ${xdlColorControlBorder};
    }

    outline-offset: ${spaceXXS};
    outline: ${scale010} solid ${xdlColorFocusRing};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  &:hover {
    border-block-end: ${scale010} solid ${xdlColorControlBorderSecondary};

    @media (prefers-contrast: more) {
      border-width: ${scale010};
      box-shadow: inset 0 0 0 ${scale005} ${xdlColorControlBorderSecondary};
    }
  }
`;

// *** Sizes ***
export const sizes = {
  small: {
    dropdown: css`
      font-size: ${scale070};
      height: ${scale150};
      padding-block-end: 5px;
      padding-block-start: 5px;
    `,
    placeholder: css`
      font-size: ${scale070};
    `,
    label: css`
      font-size: ${scale070};
      inset-block-start: ${spaceM};
    `,
  },
  medium: {
    dropdown: css`
      font-size: ${scale080};
      height: ${scale160};
      padding-block-end: ${scale050};
      padding-block-start: ${scale050};
    `,
    placeholder: css`
      font-size: ${scale080};
    `,
    label: css`
      font-size: ${scale080};
      inset-block-start: ${scale070};
    `,
  },
  large: {
    dropdown: css`
      font-size: ${scale080};
      height: ${scale170};
      padding-block-end: ${scale060};
      padding-block-start: ${scale060};
    `,
    placeholder: css`
      font-size: ${scale080};
    `,
    label: css`
      font-size: ${scale080};
      inset-block-start: ${spaceL};
    `,
  },
};

// *** State styles ***
const filledStyle = css`
  background-color: ${xdlColorControlFill};
  border-block-end: ${scale010} solid ${xdlColorControlBorder};
  border-block-start: ${scale005} solid ${xdlColorControlBorder};
  border-inline-end: ${scale005} solid ${xdlColorControlBorder};
  border-inline-start: ${scale005} solid ${xdlColorControlBorder};
  color: ${xdlColorText};
`;

const errorStyle = css`
  border-block-end: ${scale010} solid ${xdlColorError};

  &:hover,
  &:focus {
    border-block-end: ${scale010} solid ${xdlColorControlBorderSecondary};
  }
`;

const successStyle = css`
  border-block-end: ${scale010} solid ${xdlColorSuccess};

  &:hover,
  &:focus {
    border-block-end: ${scale010} solid ${xdlColorControlBorderSecondary};
  }
`;

const disabledStyle = css`
  background-color: ${xdlColorControlFill};
  border-block-end-width: ${scale010};
  border-color: ${xdlColorDisabledSoft};
  border-style: solid;
  color: ${xdlColorDisabledStrong};

  &:hover {
    background-color: ${xdlColorControlFill};
    border-block-end-width: ${scale010};
    border-color: ${xdlColorDisabledSoft};
    border-style: solid;

    @media (prefers-contrast: more) {
      box-shadow: none;
    }
  }
`;

const disabledUnfilledStyle = css`
  background-color: ${xdlColorDisabledSoft};
  border: none;
  color: ${xdlColorDisabledStrong};

  &:hover {
    background-color: ${xdlColorDisabledSoft};
    border: none;

    @media (prefers-contrast: more) {
      box-shadow: none;
    }
  }
`;

const disabledPlainStyle = css`
  background-color: ${xdlColorControlFill};
  border-block-end: ${scale010} solid ${xdlColorDisabledSoft};
  border-block-start: ${scale005} solid ${xdlColorDisabledSoft};
  border-inline-end: ${scale005} solid ${xdlColorDisabledSoft};
  border-inline-start: ${scale005} solid ${xdlColorDisabledSoft};

  &:hover {
    background-color: ${xdlColorControlFill};
  }

  &:hover,
  &:focus {
    border-block-end: ${scale010} solid ${xdlColorDisabledSoft};
    border-block-start: ${scale005} solid ${xdlColorDisabledSoft};
    border-inline-end: ${scale005} solid ${xdlColorDisabledSoft};
    border-inline-start: ${scale005} solid ${xdlColorDisabledSoft};
  }
`;

const filledStyleLabel = css`
  background-color: ${xdlColorControlFill};
  font-size: ${scale060};
  height: 12px;
  inset-block-start: -9px;
  inset-inline-start: 13px;
  padding: 0 ${spaceXS} 0 ${spaceXS};
  transform: none;
`;

const placeholderStyle = css<DropdownTProps>`
  color: transparent;
  font-weight: 400;

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return sizes.small.placeholder;
      case 'medium':
        return sizes.medium.placeholder;
      case 'large':
        return sizes.large.placeholder;
      default:
        return $size satisfies undefined;
    }
  }}
`;

export const Label = styled.label<LabelTProps>`
  color: ${xdlColorTextSecondary};
  font-weight: 400;
  inset-block-end: 0;
  inset-inline-start: 17px;
  pointer-events: none;
  position: absolute;
  z-index: 1;

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return sizes.small.label;
      case 'medium':
        return sizes.medium.label;
      case 'large':
        return sizes.large.label;
      default:
        return $size satisfies undefined;
    }
  }}

  ${({ $isFilled }) => $isFilled && filledStyleLabel};

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      color: ${xdlColorDisabledStrong};
    `};

  ${({ $isDisabled, $isFilled }) =>
    $isDisabled &&
    !$isFilled &&
    css`
      border: 0;
      clip: rect(0, 0, 0, 0);
      height: 1px;
      margin: -1px;
      overflow: hidden;
      padding: 0;
      position: absolute;
      white-space: nowrap;
      width: 1px;
    `};
`;

export const Dropdown = styled.select<DropdownTProps>`
  ${styles};
  appearance: none;
  border-end-end-radius: 0;
  border-end-start-radius: 0;
  padding-inline-end: calc(${spaceXXL} + ${scale100});

  ${({ $variant }) =>
    $variant === 'plain' &&
    css`
      background-color: ${xdlColorControlFill};
      border-block-end: ${scale010} solid ${xdlColorControlBorder};
      border-block-start: ${scale005} solid ${xdlColorControlBorder};
      border-inline-end: ${scale005} solid ${xdlColorControlBorder};
      border-inline-start: ${scale005} solid ${xdlColorControlBorder};

      &:focus {
        background-color: ${xdlColorControlFill};
      }
    `};

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return sizes.small.dropdown;
      case 'medium':
        return sizes.medium.dropdown;
      case 'large':
        return sizes.large.dropdown;
      default:
        return $size satisfies undefined;
    }
  }}

  ${({ $isFilled }) => !$isFilled && placeholderStyle};
  ${({ $isFilled }) => $isFilled && filledStyle};
  ${({ $isFilled, $variant }) =>
    $isFilled &&
    $variant === 'plain' &&
    css`
      background-color: ${xdlColorBackground};
    `};

  ${({ $success }) => $success && successStyle};
  ${({ $error }) => $error && errorStyle};

  ${({ $isDisabled }) => $isDisabled && disabledStyle};
  ${({ $isDisabled, $variant }) =>
    $isDisabled && $variant === 'plain' && disabledPlainStyle};

  ${({ $isDisabled, $isFilled }) =>
    $isDisabled && !$isFilled && disabledUnfilledStyle};
  ${({ $isDisabled, $isFilled, $variant }) =>
    $isDisabled && !$isFilled && $variant === 'plain' && disabledPlainStyle};

  &:-ms-expand {
    display: none;
  }

  & option {
    color: initial;
  }
`;

export const Placeholder = styled(OptionXdl)``;

export const Icon = styled(IconArrowDown)<IconTProps>`
  color: ${xdlColorTextSecondary};
  inset-block-start: 50%;
  inset-inline-end: ${scale100};
  pointer-events: none;
  position: absolute;

  ${({ $size }) =>
    $size === 'small'
      ? css`
          height: ${scale100};
          margin-block-start: calc(${scale100} / -2);
          width: ${scale100};
        `
      : css`
          height: ${scale110};
          margin-block-start: calc(${scale110} / -2);
          width: ${scale110};
        `};

  ${({ $isFilled }) =>
    $isFilled &&
    css`
      color: ${xdlColorText};
    `};

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      color: ${xdlColorDisabledStrong};
    `};
`;

export const HelperText = styled(Meta)<HelperTextTProps>`
  align-items: flex-start;
  color: ${xdlColorTextSecondary};
  display: flex;
  margin-block-start: ${spaceXS};
  transform: translateY(0);

  ${({ $error }) =>
    $error &&
    css`
      /* Override Meta's doubled selector even when its CSS loads later. */
      &&& {
        color: ${xdlColorError};
      }
    `};

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      &&& {
        color: ${xdlColorDisabledStrong};
      }
    `}
`;

export const ErrorIcon = styled(IconWarning)`
  flex-shrink: 0;
  height: ${scale060};
  margin-inline-end: ${spaceXXS};
  width: ${scale060};
`;

export const SuccessIcon = styled(IconTick)`
  flex-shrink: 0;
  height: ${scale060};
  margin-inline-end: ${spaceXXS};
  width: ${scale060};
`;
